import AboutSection from "@/components/AboutSection";
import BlogSection from "@/components/BlogSection";
import ContactSection from "@/components/ContactSection";
import FaqSection, { faqItems } from "@/components/FaqSection";
import Footer from "@/components/Footer";
import { Helmet } from "react-helmet";
import HeroSection from "@/components/HeroSection";
import IndustriesSection from "@/components/IndustriesSection";
import Navbar from "@/components/Navbar";
import ServicesSection from "@/components/ServicesSection";

const SITE_URL = "https://wiiseng.vercel.app";
const OG_IMAGE = `${SITE_URL}/og-image.png`;
const IMAGE_ALT =
  "Wii Seng Technology Resources - CNC machine repair and maintenance in Shah Alam, Malaysia";
const PAGE_TITLE =
  "CNC Machine Repair & Maintenance in Shah Alam, Malaysia | Wii Seng Technology";
const PAGE_DESCRIPTION =
  "CNC machine repair, maintenance, and retrofitting in Shah Alam, Malaysia. 20+ years servicing multi-axis CNC mills, lathes, grinders, and EDM machines across the Klang Valley.";

const Index = () => {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#business`,
    "name": "Wii Seng Technology Resources",
    "image": OG_IMAGE,
    "logo": `${SITE_URL}/favicon.ico`,
    "description":
      "CNC machine repair, maintenance, and retrofitting services for manufacturers and machine shops in Shah Alam, Selangor and across the Klang Valley.",
    "url": SITE_URL,
    "telephone": "+60122443138",
    "priceRange": "$$",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+60122443138",
      "contactType": "customer service",
      "areaServed": "MY",
      "availableLanguage": ["en", "zh"],
    },
    "knowsLanguage": ["en", "zh"],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "42, Jalan Sungai Merbau 32/89",
      "addressLocality": "Kota Kemuning, Shah Alam",
      "addressRegion": "Selangor",
      "postalCode": "40460",
      "addressCountry": "MY",
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "3.0167",
      "longitude": "101.5500",
    },
    "areaServed": [
      { "@type": "City", "name": "Shah Alam" },
      { "@type": "City", "name": "Kuala Lumpur" },
      { "@type": "AdministrativeArea", "name": "Klang Valley" },
      { "@type": "AdministrativeArea", "name": "Selangor" },
      { "@type": "Country", "name": "Malaysia" },
    ],
    "serviceType": [
      "CNC machine repair",
      "CNC machine maintenance",
      "CNC machine retrofitting",
      "Spindle repair",
      "Machine calibration",
      "Preventive maintenance",
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "CNC Repair & Maintenance Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "CNC Machine Servicing",
            "description":
              "Repair and servicing for multi-axis CNC mills, lathes, grinders, and EDM machines.",
          },
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Maintenance & Repair",
            "description":
              "Routine maintenance, repair, and troubleshooting for CNC machines.",
          },
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "System Diagnostics",
            "description":
              "Diagnostics and preventive maintenance to reduce downtime.",
          },
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Retrofitting & Calibration",
            "description":
              "Machine retrofitting, calibration, and spindle repair services.",
          },
        },
      ],
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
        ],
        "opens": "08:30",
        "closes": "17:30",
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "09:00",
        "closes": "13:00",
      },
    ],
    "sameAs": [
      "https://wa.me/60122443138",
      "https://share.google/XtrAbC0udnXLbmTUa",
    ],
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    "url": SITE_URL,
    "name": "Wii Seng Technology Resources",
    "inLanguage": ["en", "zh"],
    "publisher": { "@id": `${SITE_URL}/#business` },
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${SITE_URL}/#webpage`,
    "url": `${SITE_URL}/`,
    "name": PAGE_TITLE,
    "description": PAGE_DESCRIPTION,
    "inLanguage": ["en", "zh"],
    "isPartOf": { "@id": `${SITE_URL}/#website` },
    "about": { "@id": `${SITE_URL}/#business` },
    "primaryImageOfPage": {
      "@type": "ImageObject",
      "url": OG_IMAGE,
      "width": 1200,
      "height": 630,
      "caption": IMAGE_ALT,
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta
          name="keywords"
          content="CNC machine repair Malaysia, CNC maintenance, CNC retrofitting, spindle repair, EDM machine service, machine calibration, Shah Alam, Kota Kemuning, Klang Valley, Selangor"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Wii Seng Technology Resources" />
        <meta name="geo.region" content="MY-10" />
        <meta name="geo.placename" content="Shah Alam, Selangor" />

        <link rel="canonical" href={`${SITE_URL}/`} />
        <link rel="alternate" hrefLang="en-MY" href={`${SITE_URL}/`} />
        <link rel="alternate" hrefLang="zh" href={`${SITE_URL}/`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/`} />
        <link rel="manifest" href="/site.webmanifest" />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Wii Seng Technology Resources" />
        <meta property="og:locale" content="en_MY" />
        <meta property="og:locale:alternate" content="zh_CN" />
        <meta property="og:url" content={`${SITE_URL}/`} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={IMAGE_ALT} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`${SITE_URL}/`} />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content={IMAGE_ALT} />

        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      <Navbar />
      <main>
        <HeroSection />
        <ServicesSection />
        <IndustriesSection />
        <AboutSection />
        <FaqSection />
        <BlogSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
